import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { REPLY_MAX_LEN, REVIEW_PATHS } from "../../reviews/contract";
import { reviewErrorText } from "../../reviews/errorText";
import { fmtDate } from "../../reviews/format";
import { REVIEWS } from "../../constants/testIds/reviews";

/**
 * The practice's ONE public answer to a review.
 *
 * IMMUTABLE BY CONSTRUCTION, NOT BY A DISABLED BUTTON. The server decides with a CAS on
 * `reply: null` and answers `REVIEW_ALREADY_REPLIED` to the loser, so two tabs cannot each
 * believe they wrote the reply. This component mirrors that in the only honest way: a review
 * that HAS a reply renders it as text with no form at all — there is nothing to offer, so
 * nothing is offered. A form that submitted and then said "too late" would be a control that
 * exists to fail.
 *
 * `reload()` AFTER THE WRITE rather than a local splice of the response: the row the list
 * holds is the list's, and a second reader (another tab, the operator hiding it a second
 * later) makes the server's copy the only one worth rendering.
 *
 * @param {object} review an `owner_review` row
 * @param {function} t `useT("admin")`
 * @param {function} tv `useT("common")`
 * @param {string} lang
 * @param {() => void} onReplied re-read the list
 */
export default function ReplyEditor({ review, t, tv, lang, onReplied }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  if (review.reply) {
    return (
      <div className="mt-3 rounded-xl border border-eh-border-soft bg-eh-cream/50 p-3">
        <p className="text-[11px] uppercase tracking-wider text-eh-muted mb-1">{t("rev.reply.yours")}</p>
        <p
          className="text-sm text-eh-ink/80 whitespace-pre-line break-words m-0"
          data-testid={REVIEWS.adminReplyText(review.id)}
        >
          {review.reply.text}
        </p>
        <p className="text-[11px] text-eh-muted tabular-nums mt-1">{fmtDate(review.reply.at, lang)}</p>
      </div>
    );
  }

  const submit = async () => {
    const body = text.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      await api.post(REVIEW_PATHS.tenantReply(review.id), { text: body });
      toast.success(t("rev.reply.saved"));
      setText("");
      if (onReplied) onReplied();
    } catch (e) {
      toast.error(reviewErrorText(e, { t, tv, fallback: tv("errors.saveFailed") }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3">
      <label htmlFor={`reply-${review.id}`} className="block text-xs text-eh-muted mb-1">
        {t("rev.reply.label")}
      </label>
      <textarea
        id={`reply-${review.id}`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        maxLength={REPLY_MAX_LEN}
        rows={3}
        disabled={busy}
        placeholder={t("rev.reply.placeholder")}
        data-testid={REVIEWS.adminReplyInput(review.id)}
        className="w-full rounded-xl border border-eh-border-soft bg-white p-3 text-sm text-eh-ink focus:outline-none focus:border-eh-sage-deep"
      />
      <button
        type="button"
        onClick={submit}
        disabled={busy || !text.trim()}
        data-testid={REVIEWS.adminReplySubmit(review.id)}
        className="mt-2 inline-flex items-center px-4 h-9 rounded-full bg-eh-sage-deep text-white text-sm font-medium hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {busy ? t("rev.reply.submitting") : t("rev.reply.submit")}
      </button>
    </div>
  );
}
